'use client';

import BookSvg from '@/assets/svgs/main/contact-lists-fill.svg';
import PlusIconSvg from '@/assets/svgs/plus.svg';
import InfoAlert from '@/shared/components/InfoAlert/InfoAlert';
import {
  useFetchContactLists,
  useFetchPendingContactLists,
} from '@/containers/ContactLists/api';
import { selectShouldRefreshList } from '@/containers/ContactLists/redux/contactLists.selectors';
import { setShouldRefreshList } from '@/containers/ContactLists/redux/contactLists.slice';
import { ContactList } from '@/containers/ContactLists/types';
import {
  selectAssignedContactLists,
  selectUsesRateplan,
} from '@/containers/Jobs/redux/jobEditors.selectors';
import {
  setAssignContactList,
  setUnassignContactList,
} from '@/containers/Jobs/redux/jobEditors.slice';
import { Modals } from '@/containers/Modals/all-modals';
import { useAppDispatch } from '@/lib/redux/store';
import { AssignmentList } from '@/shared/components/AssignmentList';
import { SearchBar } from '@/shared/components/SearchBar/SearchBar';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { addOpenModal } from '../redux/modals.slice';
import { AssignContactListModalParams, ModalProps, P2PUploadParams } from '../types';
import useModals from '../useModals';
import ContactListItemView from './ContactListItemView';

interface AssignContactListModalProps extends ModalProps {
  params?: AssignContactListModalParams;
  className?: string;
}

const modalName = Modals.AssignContactListModal;
const AssignContactListModal = ({
  className,
  params,
  variant = 'default',
}: AssignContactListModalProps) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [isSearchEmpty, setIsSearchEmpty] = useState(true);
  const { isModalOpen, onClose } = useModals({
    modalName,
  });

  // Modal Specific details
  const dispatch = useAppDispatch();
  const jobId = params?.jobId || '';
  const identityId = params?.identityId;
  const jobUsesRateplan = useSelector(selectUsesRateplan(jobId));

  const {
    isLoading: isLoadingContactLists,
    data: contactLists,
    refetch: refetchContactLists,
  } = useFetchContactLists(identityId, !!identityId, jobUsesRateplan);

  const {
    isLoading: isLoadingPendingContactLists,
    data: pendingContactLists,
    // data: contactLists,
    refetch: refetchPendingContactLists,
  } = useFetchPendingContactLists(identityId, !!identityId, jobUsesRateplan);

  const combinedList = useMemo(
    () => [...(pendingContactLists?.data ?? []), ...(contactLists?.data ?? [])],
    [contactLists?.data, pendingContactLists?.data]
  );

  const [searchText, setSearchText] = useState('');
  const [isShowingCompletedLists, setIsShowingCompletedLists] = useState(false);

  const [searchResults, setSearchResults] = useState<ContactList[]>([]);

  // Refresh list
  const shouldRefreshList = useSelector(selectShouldRefreshList);
  useEffect(() => {
    if (shouldRefreshList) {
      refetchContactLists();
      refetchPendingContactLists();
      dispatch(setShouldRefreshList(false));
    }
  }, [dispatch, refetchContactLists, refetchPendingContactLists, shouldRefreshList]);

  useEffect(() => {
    setIsSearchEmpty(searchText === '');
    if (!searchText) {
      return setSearchResults(combinedList);
    }

    setSearchResults(
      combinedList.filter((item) =>
        item.list_name.toLowerCase().includes(searchText.toLowerCase())
      ) ?? []
    );
  }, [searchText, combinedList]);

  const assignedContactListItems = useSelector(selectAssignedContactLists(jobId));
  const assignedContactLists = useMemo(() => {
    return assignedContactListItems
      .map(
        (item) =>
          contactLists?.data.find((cl) => cl.list_id === item.list_id) as ContactList
      )
      .filter(Boolean);
  }, [contactLists?.data, assignedContactListItems]);

  // Available contact lists are Search results (including contact lists API response, and pending lists API response) Minus Assigned contact lists
  const availableContactLists = useMemo(() => {
    const assignedContactListIds = assignedContactListItems.map((list) => list.list_id);
    return (
      searchResults
        .filter((item) => !assignedContactListIds.includes(item.list_id))
        // Deferred / awaiting-check lists are not assignable, so don't surface them
        .filter(
          (item) => item.list_state !== 'DEFERRED' && item.list_state !== 'AWAITING-CHECK'
        )
        .filter((item) =>
          // filter showing / hiding only for ACTIVE lists
          item.list_state === 'ACTIVE'
            ? isShowingCompletedLists
              ? true
              : !!item.leads_remaining
            : true
        )
        .filter((item) => {
          // Filter by spotlight match - only show lists that match the job's spotlight setting
          const itemUsesRateplan = item.uses_rateplan === 1;
          return itemUsesRateplan === jobUsesRateplan;
        })
        .filter(Boolean)
    );
  }, [assignedContactListItems, searchResults, isShowingCompletedLists, jobUsesRateplan]);

  const handleAssign = (contactList: ContactList) => {
    dispatch(
      setAssignContactList({
        jobId,
        assignment: {
          list_id: contactList.list_id,
          deduplicate: false,
        },
      })
    );
  };

  const handleUnassign = (contactList: ContactList) => {
    dispatch(
      setUnassignContactList({
        jobId,
        listId: contactList.list_id,
      })
    );
  };

  const unmount = () => {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => unmount, []);

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />
      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        isSearchEmpty={isSearchEmpty}
        variant={variant}
        onClose={onClose}
        shouldCloseOnEscape>
        <div className="flex h-full flex-col overflow-hidden">
          {/* Stick to top */}
          <ModalHeader
            modalName={modalName}
            closeAction="state"
            icon={<BookSvg className="h-5 w-5" />}
            title="Contact Lists"
          />

          {/* Take up remaining space and enable inside-scrolling */}
          <ModalBody ref={modalBodyRef} className="h-full px-5 !py-0">
            {/* Action button */}
            <div className="flex h-[84px] flex-col items-end justify-center gap-2">
              {jobUsesRateplan && (
                <InfoAlert className="py-2">
                  <span className="text-xs">
                    This job uses Spotlight. Only Spotlight-enabled contact lists can be
                    assigned.
                  </span>
                </InfoAlert>
              )}
              <button
                className="btn btn-primary btn-sm flex gap-1 pl-1.5"
                onClick={() => {
                  dispatch(
                    addOpenModal({
                      modalName: Modals.P2PUploadModal,
                      modalVariant: 'narrow',
                      modalParams: JSON.stringify({
                        type: 'p2p-sms',
                        hideOtherListTypes: true,
                        identityId: identityId,
                      } as P2PUploadParams),
                    })
                  );
                }}>
                <PlusIconSvg className="h-5 w-5" />
                New Contacts
              </button>
            </div>

            {/* Search */}
            <div className="search-container mb-6">
              <SearchBar
                search={searchText}
                setSearch={setSearchText}
                placeholder="Search contact lists..."
                debounceMs={500}
                ariaLabel="Search contact lists"
                className="w-full"
              />
            </div>

            {/* Show/Hide completed lists toggle */}
            <div className="mb-2 flex justify-end">
              <button
                className="btn btn-ghost btn-xs"
                onClick={() => setIsShowingCompletedLists(!isShowingCompletedLists)}>
                {isShowingCompletedLists
                  ? 'hide completed lists'
                  : 'show completed lists'}
              </button>
            </div>

            {/* Assignment List */}
            <AssignmentList
              assignedItems={assignedContactLists}
              availableItems={availableContactLists}
              LineItemView={ContactListItemView}
              onAssign={handleAssign}
              onUnassign={handleUnassign}
              isLoading={isLoadingContactLists || isLoadingPendingContactLists}
              searchText={searchText}
              emptyMessage={
                <div className="flex flex-col gap-3">
                  <span>No Contact Lists found</span>
                  {searchText && (
                    <button
                      className="btn btn-outline btn-sm border-gray-200 shadow-sm"
                      onClick={() => setSearchText('')}>
                      Reset search
                    </button>
                  )}
                </div>
              }
              getItemKey={(item) =>
                item.list_id
                  ? item.list_id
                  : `pending-${item.list_name}-${item.upload_date || item.uploaded_date || Math.random()}`
              }
              assignButtonLabel="Assign"
              unassignButtonLabel="Unassign"
            />
          </ModalBody>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </ModalContainer>
    </div>
  );
};

export default AssignContactListModal;
